import { QueryClient, QueryFunction, MutationCache } from "@tanstack/react-query";
import { notifySaved } from "@/lib/saved-indicator";
import { logoutOneSignalIdentity } from "@/lib/onesignal-native";

/**
 * Extract per-field validation messages from an apiRequest error thrown
 * by `throwIfResNotOk`. Returns a `{ field: message }` map keyed by the
 * first path segment of each Zod issue, or an empty object if the error
 * is not a 400 with a parseable Zod-style errors array.
 */
export function parseFieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof Error)) return {};
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  if (!match || Number(match[1]) !== 400) return {};
  try {
    const parsed = JSON.parse(match[2]);
    if (!Array.isArray(parsed?.errors)) return {};
    const out: Record<string, string> = {};
    for (const issue of parsed.errors) {
      const path = Array.isArray(issue?.path) ? issue.path[0] : issue?.path;
      const field = path != null ? String(path) : "";
      if (field && !out[field] && typeof issue?.message === "string") {
        out[field] = issue.message;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Extract the HTTP status code from an apiRequest / getQueryFn error.
 *
 * Both `throwIfResNotOk` and the default query fetcher throw
 * `new Error(`${status}: ${body}`)`, so the leading three digits encode the
 * response status. Returns the numeric status, or `null` when the error is
 * not an Error or carries no parseable status prefix.
 */
export function parseErrorStatus(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/^(\d{3}):/);
  return match ? Number(match[1]) : null;
}

/**
 * True when the error represents a 401 (not authenticated / session expired).
 * Callers should treat this as a session/auth failure and send the user to
 * log in rather than showing an in-page permission message.
 */
export function isUnauthorizedError(err: unknown): boolean {
  return parseErrorStatus(err) === 401;
}

/**
 * True when the error represents a 403 (authenticated but not permitted).
 * Callers should keep the user logged in and surface a clear "you don't have
 * permission" message instead of bouncing them to the login screen.
 */
export function isForbiddenError(err: unknown): boolean {
  return parseErrorStatus(err) === 403;
}

/**
 * Try to parse the JSON body carried by an apiRequest / getQueryFn error.
 * Both throw `new Error(`${status}: ${body}`)`, so we strip the leading
 * `${status}: ` prefix and JSON.parse what remains. Returns the parsed object
 * (as a loosely-typed record) or `null` when the body is missing or not JSON.
 * Use this when a route returns a structured error payload (e.g. the
 * duplicate-phone 409 that carries the existing vendor's public fields).
 */
export function parseErrorBody(err: unknown): Record<string, any> | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  const body = match ? match[2] : err.message;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === "object") return parsed as Record<string, any>;
  } catch {}
  return null;
}

/**
 * Try to extract a top-level `{ code }` string from an apiRequest error body.
 * Returns null if no code can be parsed.
 */
export function parseErrorCode(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  const body = match ? match[2] : err.message;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed.code === "string") return parsed.code;
  } catch {}
  return null;
}

/**
 * True when the error represents a 403 whose body carries
 * `code: "UPGRADE_REQUIRED"` — i.e. the user reached a menu section their plan
 * doesn't include (server enforcement in `server/lib/menu-tier-locks.ts`).
 * Callers can use this to suppress their raw error toast and let the global
 * upgrade prompt (see `subscribeUpgradeRequired` + `UpgradeRequiredListener`)
 * surface the upgrade CTA instead.
 */
export function isUpgradeRequiredError(err: unknown): boolean {
  return parseErrorStatus(err) === 403 && parseErrorCode(err) === "UPGRADE_REQUIRED";
}

/**
 * Extract the locked menu-tier-lock `key` (e.g. `operations.tasks`) carried by a
 * 403 `UPGRADE_REQUIRED` error body, or `null` when the error isn't an upgrade
 * error or carries no key. Pages use this to resolve the locked section's label
 * + required tier for an inline "Upgrade to unlock" panel (Task #4441), the same
 * `{ code, key }` body the global `UpgradeRequiredListener` consumes.
 */
export function parseUpgradeRequiredKey(err: unknown): string | null {
  if (!isUpgradeRequiredError(err) || !(err instanceof Error)) return null;
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  const body = match ? match[2] : err.message;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed.key === "string") return parsed.key;
  } catch {}
  return null;
}

/**
 * Try to extract `code: "RATE_LIMITED"` and the `retryAfterMs` hint from a
 * 429 apiRequest error body. Returns `null` if the body cannot be parsed
 * or is not a rate-limit response. The conversation composer (Task #951)
 * uses this to drive a friendly toast + countdown banner on the messaging
 * UI when an attachment-bearing send is blocked by the per-user URL or
 * bytes-per-hour quota.
 */
export function parseRateLimitInfo(
  err: unknown,
): { message: string; retryAfterMs: number } | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  if (!match || Number(match[1]) !== 429) return null;
  try {
    const parsed = JSON.parse(match[2]);
    if (!parsed || parsed.code !== "RATE_LIMITED") return null;
    const retryAfterMs =
      typeof parsed.retryAfterMs === "number" && parsed.retryAfterMs > 0
        ? parsed.retryAfterMs
        : 0;
    const message =
      typeof parsed.message === "string" && parsed.message.length > 0
        ? parsed.message
        : "Please wait before trying again.";
    return { message, retryAfterMs };
  } catch {
    return null;
  }
}

/**
 * Try to extract a top-level `{ errorId }` string from an apiRequest error
 * body. Returns the Sentry event ID (Task #759) when present, otherwise
 * `null`. Used by `parseErrorMessage` to append a tracking ID to the user-
 * visible message and by the toast layer for prominent display.
 */
export function parseErrorId(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  const body = match ? match[2] : err.message;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed.errorId === "string" && parsed.errorId.length > 0) {
      return parsed.errorId;
    }
  } catch {}
  return null;
}

/**
 * Try to extract a top-level `{ message }` string from an apiRequest error.
 * Falls back to the raw error message if the body cannot be parsed.
 *
 * Task #759: when the server attaches an `errorId` (Sentry event ID) to
 * the response — currently only on 5xx errors when `SENTRY_DSN` is
 * configured — the helper appends `\n\nError ID: <id>` to the message so
 * any caller that already pipes the result into a toast description
 * automatically shows users a tracking ID for support handoff. Bodies
 * without `errorId` keep returning the bare message, preserving the
 * historical contract.
 */
export function isImpersonationCancelled(err: unknown): boolean {
  return err instanceof ImpersonationCancelledError;
}

export function parseErrorMessage(err: unknown, fallback?: string): string {
  if (err instanceof ImpersonationCancelledError) return "";
  if (!(err instanceof Error)) return fallback ?? "Something went wrong";
  const match = err.message.match(/^(\d{3}):\s*([\s\S]*)$/);
  const body = match ? match[2] : err.message;
  let message: string | null = null;
  let errorId: string | null = null;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed.message === "string") {
      message = parsed.message;
    } else if (parsed && typeof parsed.error === "string") {
      message = parsed.error;
    }
    if (parsed && typeof parsed.errorId === "string" && parsed.errorId.length > 0) {
      errorId = parsed.errorId;
    }
  } catch {}
  const base = message ?? fallback ?? err.message;
  return errorId ? `${base}\n\nError ID: ${errorId}` : base;
}

export const SESSION_REVOKED_STORAGE_KEY = "padpulse:session-revoked";

export type SessionRevokedNotice = {
  reason: string;
  message: string;
  ownerName: string | null;
  ownerEmail: string | null;
  removedAt: string | null;
};

function maybeHandleRevokedResponse(status: number, text: string): boolean {
  if (status !== 401 || !text) return false;
  try {
    const body = JSON.parse(text);
    if (body && body.code === "SESSION_REVOKED") {
      const notice: SessionRevokedNotice = {
        reason: typeof body.reason === "string" ? body.reason : "revoked",
        message: typeof body.message === "string" ? body.message : "Your access has been revoked.",
        ownerName: typeof body.ownerName === "string" ? body.ownerName : null,
        ownerEmail: typeof body.ownerEmail === "string" ? body.ownerEmail : null,
        removedAt: typeof body.removedAt === "string" ? body.removedAt : null,
      };
      try {
        sessionStorage.setItem(SESSION_REVOKED_STORAGE_KEY, JSON.stringify(notice));
      } catch {}
      // The revoked flow unloads to /app/no-team, a route outside AppLayout,
      // so the OneSignal identity hook never observes this 401 — unbind the
      // device here (no-op outside the native wrapper) so a revoked user
      // stops receiving the account's push notifications.
      logoutOneSignalIdentity();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/app/no-team")) {
        window.location.replace("/app/no-team");
      }
      return true;
    }
  } catch {}
  return false;
}

// Task #4436 — global "upgrade required" prompt.
//
// The server returns 403 `{ error, code: "UPGRADE_REQUIRED", key }` when a user
// calls an API for a menu section their plan doesn't include (server enforcement
// in `server/lib/menu-tier-locks.ts`). The sidebar already hides locked sections
// proactively, but a user who reaches a locked section via a deep link or a
// stale tab will fire real API calls that 403. Rather than each page surfacing a
// raw error toast, we detect that response centrally here (every query and
// mutation flows through `throwIfResNotOk`) and notify subscribers so a single
// app-wide listener (`UpgradeRequiredListener`) can show a friendly upgrade CTA
// naming the locked section. Mirrors `maybeHandleRevokedResponse` above.
export type UpgradeRequiredInfo = { key: string };
type UpgradeRequiredHandler = (info: UpgradeRequiredInfo) => void;
const upgradeRequiredHandlers = new Set<UpgradeRequiredHandler>();

/**
 * Subscribe to centrally-detected 403 `UPGRADE_REQUIRED` responses. Returns an
 * unsubscribe function. Used by the app-wide `UpgradeRequiredListener`.
 */
export function subscribeUpgradeRequired(handler: UpgradeRequiredHandler): () => void {
  upgradeRequiredHandlers.add(handler);
  return () => {
    upgradeRequiredHandlers.delete(handler);
  };
}

function maybeNotifyUpgradeRequired(status: number, text: string): void {
  if (status !== 403 || !text) return;
  try {
    const body = JSON.parse(text);
    // Only the menu-tier-lock enforcers — which name a locked menu SECTION via
    // `key` (generic middleware, Pulse, Trash) — drive the global section-naming
    // upgrade modal. Key-less `UPGRADE_REQUIRED` 403s come from hard plan-tier
    // gates (`requireTier`, property-activate) that background queries on an
    // otherwise-allowed page legitimately hit — e.g. a Professional host opening
    // a task fires the PMPro-gated owners query, which should fail silently, not
    // pop a misleading "Upgrade to Professional" modal.
    if (body && body.code === "UPGRADE_REQUIRED" && typeof body.key === "string" && body.key) {
      const key: string = body.key;
      for (const handler of upgradeRequiredHandlers) {
        try {
          handler({ key });
        } catch {}
      }
    }
  } catch {}
}

// Global "subscription inactive" notice. Gated APIs answer 402
// `{ code: "SUBSCRIPTION_INACTIVE" }` when the account's billing no longer
// grants access (payment failed → past_due, trial over without a
// subscription, subscription canceled). Detected centrally here — like the
// upgrade prompt above — so one app-wide banner (`SubscriptionInactiveBanner`)
// explains the problem and links to billing / Checkout instead of every page
// rendering a raw error or an empty state.
type SubscriptionInactiveHandler = () => void;
const subscriptionInactiveHandlers = new Set<SubscriptionInactiveHandler>();
let subscriptionInactiveSeen = false;

/**
 * Subscribe to centrally-detected 402 `SUBSCRIPTION_INACTIVE` responses.
 * Returns an unsubscribe function.
 */
export function subscribeSubscriptionInactive(handler: SubscriptionInactiveHandler): () => void {
  subscriptionInactiveHandlers.add(handler);
  return () => {
    subscriptionInactiveHandlers.delete(handler);
  };
}

/**
 * Whether a 402 `SUBSCRIPTION_INACTIVE` has been seen since page load (or the
 * last reset). Lets a listener that mounts AFTER the response (AppLayout
 * remounts on every page) still show the notice.
 */
export function hasSeenSubscriptionInactive(): boolean {
  return subscriptionInactiveSeen;
}

/** Test hook — clears the sticky "seen" flag. */
export function resetSubscriptionInactiveForTest(): void {
  subscriptionInactiveSeen = false;
}

function maybeNotifySubscriptionInactive(status: number, text: string): void {
  if (status !== 402 || !text) return;
  try {
    const body = JSON.parse(text);
    if (body && body.code === "SUBSCRIPTION_INACTIVE") {
      subscriptionInactiveSeen = true;
      for (const handler of subscriptionInactiveHandlers) {
        try {
          handler();
        } catch {}
      }
    }
  } catch {}
}

// v3 per-property billing: the server answers 402 with one of three codes the
// app handles in one place (`V3BillingRequiredListener`):
//   ACCOUNT_READ_ONLY   paused / long-past-due account tried to change data
//                       → "Add a card" (Stripe-hosted card setup)
//   HOST_TRIAL_REQUIRED free Research account hit a Host feature
//                       → "Start your 14-day Host trial"
//   RESEARCH_CAP        6th free buy-box search this month
//                       → "Start your 14-day Host trial"
// `source` says whether the response came from an explicit request
// (apiRequest — mutations and user actions) or a background query fetch, so
// the listener can avoid re-prompting for polling queries.
export type V3BillingRequiredCode = "ACCOUNT_READ_ONLY" | "HOST_TRIAL_REQUIRED" | "RESEARCH_CAP";
export type V3BillingRequiredInfo = {
  code: V3BillingRequiredCode;
  message: string | null;
  source: "request" | "query";
};
type V3BillingRequiredHandler = (info: V3BillingRequiredInfo) => void;
const v3BillingRequiredHandlers = new Set<V3BillingRequiredHandler>();
const V3_BILLING_REQUIRED_CODES: readonly string[] = ["ACCOUNT_READ_ONLY", "HOST_TRIAL_REQUIRED", "RESEARCH_CAP"];

/** Subscribe to centrally-detected v3 billing 402s. Returns an unsubscribe function. */
export function subscribeV3BillingRequired(handler: V3BillingRequiredHandler): () => void {
  v3BillingRequiredHandlers.add(handler);
  return () => {
    v3BillingRequiredHandlers.delete(handler);
  };
}

function maybeNotifyV3BillingRequired(status: number, text: string, source: "request" | "query"): void {
  if (status !== 402 || !text) return;
  try {
    const body = JSON.parse(text);
    if (!body || typeof body.code !== "string" || !V3_BILLING_REQUIRED_CODES.includes(body.code)) return;
    const message =
      typeof body.error === "string" ? body.error : typeof body.message === "string" ? body.message : null;
    const info: V3BillingRequiredInfo = { code: body.code as V3BillingRequiredCode, message, source };
    for (const handler of v3BillingRequiredHandlers) {
      try {
        handler(info);
      } catch {}
    }
  } catch {}
}

/**
 * Shared non-OK handling for fetch responses. Exported so hand-written
 * queryFns (e.g. the parameterised inbox fetches on the Messages page) get the
 * same session-revoked redirect and upgrade-required prompt as every request
 * that goes through apiRequest/getQueryFn — bypassing it silently drops those
 * global behaviors for that query.
 */
export async function throwIfResNotOk(res: Response, source: "request" | "query" = "query") {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    maybeHandleRevokedResponse(res.status, text);
    maybeNotifyUpgradeRequired(res.status, text);
    maybeNotifySubscriptionInactive(res.status, text);
    maybeNotifyV3BillingRequired(res.status, text, source);
    throw new Error(`${res.status}: ${text}`);
  }
}

type ImpersonationGuardHandler = (method: string, url: string) => Promise<boolean>;
let _impersonationGuardHandler: ImpersonationGuardHandler | null = null;

export function setImpersonationGuardHandler(handler: ImpersonationGuardHandler | null) {
  _impersonationGuardHandler = handler;
}

const HIGH_RISK_URL_PATTERNS = [
  /\/send\b/,
  /\/apply\b/,
  /\/delete\b/,
  /\/remove\b/,
  /\/destroy\b/,
  /\/sync\b/,
  /\/broadcast\b/,
  /\/archive\b/,
  /\/reset-totp\b/,
];

// Transient / bookkeeping endpoints that are technically DELETE but don't
// mutate meaningful data — the guard should skip them.
const LOW_RISK_URL_PATTERNS = [
  /\/members\/\d+\/presence\b/, // presence heartbeat cleanup, not a real delete
];

export function isHighRiskAction(method: string, url: string): boolean {
  const upper = method.toUpperCase();
  if (upper === "GET" || upper === "HEAD" || upper === "OPTIONS") return false;
  if (LOW_RISK_URL_PATTERNS.some((re) => re.test(url))) return false;
  if (upper === "DELETE") return true;
  return HIGH_RISK_URL_PATTERNS.some((re) => re.test(url));
}

export class ImpersonationCancelledError extends Error {
  constructor() {
    super("Action cancelled by impersonation guard");
    this.name = "ImpersonationCancelledError";
  }
}

// Task #4132 — surface-agnostic "Messages badge" freshness nudge.
//
// The sidebar "Messages" badge counts open (unresolved) member messages
// (`/api/app/messages/unread-count`). The in-app resolve/unresolve surfaces
// already call `invalidateResolveQueries` (see
// client/src/pages/app-messages/_resolve-invalidations.ts), but that depends on
// every surface remembering to do so — an admin-impersonation view or a future
// resolve UI that forgets would leave the badge stale until the next poll.
//
// Because EVERY resolve/unresolve goes through `apiRequest`, we invalidate the
// badge-relevant queries centrally right here on any successful member-message
// resolve mutation. This makes the badge update within ~1s regardless of which
// surface fired the action, with no per-surface bookkeeping. Interval polling
// (app-layout.tsx) is only a last-resort fallback. It is safe to fire even when
// the surface ALSO calls invalidateResolveQueries — React Query dedupes the
// resulting refetch.
//
// Matches the member-message resolve endpoints only:
//   POST /api/app/messages/:id/resolve
//   POST /api/app/messages/:id/unresolve
//   POST /api/app/members/:memberId/messages/resolve-all
//   POST /api/app/members/:memberId/messages/unresolve
// (Team-inbox / todo resolves use different paths and don't move this count.)
const MEMBER_MESSAGE_RESOLVE_URL_RE =
  /\/messages\/(?:\d+\/)?(?:un)?resolve(?:-all)?(?:$|\?|\/)/;

function nudgeMessagesBadgeAfterResolve(method: string, url: string) {
  const upper = method.toUpperCase();
  if (upper === "GET" || upper === "HEAD" || upper === "OPTIONS") return;
  if (!MEMBER_MESSAGE_RESOLVE_URL_RE.test(url)) return;
  // Mirror invalidateResolveQueries' account-level keys (timeline is per-member
  // and the firing surface owns that). Best-effort — never let a cache nudge
  // surface an error to the caller.
  try {
    queryClient.invalidateQueries({ queryKey: ["/api/app/messages/unread-count"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/messages/unresolved"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/inbox?unresolvedOnly=1"] });
    // Task #4340 — the unresolved-count toggle badge. Its key is a distinct full
    // string (not a prefix of ["/api/app/inbox"]) so it needs its own line.
    queryClient.invalidateQueries({ queryKey: ["/api/app/inbox?countOnly=1"] });
  } catch {}
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { signal?: AbortSignal },
): Promise<Response> {
  if (_impersonationGuardHandler && isHighRiskAction(method, url)) {
    const confirmed = await _impersonationGuardHandler(method, url);
    if (!confirmed) throw new ImpersonationCancelledError();
  }

  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    signal: options?.signal,
  });

  await throwIfResNotOk(res, "request");
  nudgeMessagesBadgeAfterResolve(method, url);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (res.status === 401) {
      const text = await res.clone().text();
      if (maybeHandleRevokedResponse(401, text)) {
        if (unauthorizedBehavior === "returnNull") return null;
        throw new Error(`401: ${text}`);
      }
      if (unauthorizedBehavior === "returnNull") return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  // HOST APP ONLY — the "Saved" pill must never fire in the vendor portal or
  // partner portal. The vendor portal is rendered inside the main host
  // QueryClientProvider (App.tsx), so every vendor-portal mutation MUST carry
  // `meta: { silentSave: true }` to suppress the pill there. The partner
  // portal is fully isolated: partner-main.tsx creates its own QueryClient
  // with no MutationCache hook, so its mutations never reach this callback.
  //
  // Task #2874: fire the global "Saved" indicator on every successful mutation
  // across the host app, without having to touch any individual page.
  // Task #2878: skip mutations tagged with meta: { silentSave: true } — these
  // are background/non-save operations (read-marking, logout, impersonation,
  // sync triggers, and all vendor-portal mutations) that must not show the
  // "Saved" pill to the user.
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.options.meta?.silentSave) return;
      notifySaved();
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Reset the entire query cache when a session boundary is crossed (login,
 * verify-code, TOTP success, or an auth-page probe that finds a live session).
 *
 * Why this exists: queries default to `staleTime: Infinity`, so anything
 * fetched while unauthenticated — most importantly the `/api/app/auth/me`
 * 401 error cached by identity hooks that run on the login page — stays
 * cached forever. AppLayout gates every /app page on that query, so after a
 * login it would render nothing (or bounce back to login) until a hard
 * refresh. Clearing at the session boundary also prevents any cross-user
 * cache leakage between logins.
 */
export function resetSessionCache() {
  queryClient.clear();
}

// Task #1434: Keep the Setup page's onboarding-status query fresh whenever a
// mutation invalidates one of the underlying resources it depends on. This
// avoids needing every mutation site to add an extra invalidation, and means
// the Setup page (and any future progress badge) always reflects the latest
// state without page reloads.
const SETUP_STATUS_KEY = "/api/app/setup-status";
const SETUP_STATUS_TRIGGER_KEYS = new Set<string>([
  "/api/app/properties",
  "/api/app/rooms",
  "/api/app/pricing-models",
  "/api/app/team-members",
  "/api/app/devices/connections",
]);

queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated") return;
  // Only react to invalidations (transition into stale + invalidated state).
  // Without this guard we would loop on the very invalidation we trigger.
  const action = (event as { action?: { type?: string } }).action;
  if (!action || action.type !== "invalidate") return;
  const queryKey = event.query.queryKey;
  const first = Array.isArray(queryKey) ? queryKey[0] : queryKey;
  if (typeof first !== "string") return;
  if (first === SETUP_STATUS_KEY) return;
  if (!SETUP_STATUS_TRIGGER_KEYS.has(first)) return;
  queryClient.invalidateQueries({ queryKey: [SETUP_STATUS_KEY] });
});
